import React, { useState } from 'react';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  FileText, 
  Edit3, 
  Upload, 
  BarChart3, 
  Eye, 
  BookOpen, 
  Settings,
  Activity,
  Clock,
  TrendingUp,
  CheckCircle2,
  AlertCircle,
  Star,
  MessageSquare,
  ThumbsUp
} from 'lucide-react';

type ContentTabType = 'dashboard' | 'create' | 'manage' | 'review' | 'analytics' | 'settings';

export function ContentDashboard() {
  const [activeTab, setActiveTab] = useState<ContentTabType>('dashboard');

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Activity, description: 'Content Overview & Stats' },
    { id: 'create', label: 'Create Content', icon: Edit3, description: 'Write & Create New Content' },
    { id: 'manage', label: 'Manage Content', icon: FileText, description: 'Edit & Organize Content' },
    { id: 'review', label: 'Review Queue', icon: Eye, description: 'Pending Reviews & Feedback' },
    { id: 'analytics', label: 'Analytics', icon: BarChart3, description: 'Performance & Engagement' },
    { id: 'settings', label: 'Settings', icon: Settings, description: 'Creator Preferences' }
  ];

  const contentStats = [
    { label: 'Published Content', value: '127', icon: FileText, color: 'bg-blue-50', trend: '+8' },
    { label: 'Total Views', value: '12.4K', icon: Eye, color: 'bg-green-50', trend: '+15%' },
    { label: 'In Review', value: '5', icon: Clock, color: 'bg-yellow-50', trend: '+2' },
    { label: 'Average Rating', value: '4.8', icon: Star, color: 'bg-purple-50', trend: '+0.2' }
  ];

  const recentContent = [
    { 
      title: 'Introduction to Ethics', 
      status: 'published', 
      views: '1.2K', 
      rating: 4.9, 
      lastEdited: '2 days ago',
      engagement: 85 
    },
    { 
      title: 'Logic and Reasoning Fundamentals', 
      status: 'in-review', 
      views: '892', 
      rating: 4.7, 
      lastEdited: '1 day ago',
      engagement: 78 
    },
    { 
      title: 'Philosophy of Mind', 
      status: 'draft', 
      views: '0', 
      rating: 0, 
      lastEdited: '3 hours ago',
      engagement: 0 
    }
  ];

  const notifications = [
    { message: 'Your content "Ethics in Practice" has been approved', type: 'success', time: '1 hour ago' },
    { message: 'Review requested for "Critical Thinking Methods"', type: 'info', time: '3 hours ago' },
    { message: 'High engagement on "Philosophy Basics"', type: 'success', time: '1 day ago' }
  ];

  const renderMainContent = () => {
    switch (activeTab) {
      case 'create':
        return (
          <div className="space-y-6">
            <Card className="bg-white border border-gray-200">
              <CardHeader>
                <CardTitle className="text-black">Create New Content</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button className="h-auto flex-col gap-2 p-6">
                    <Edit3 className="h-8 w-8" />
                    <span>Write Article</span>
                  </Button>
                  <Button variant="outline" className="h-auto flex-col gap-2 p-6">
                    <Upload className="h-8 w-8" />
                    <span>Upload Resource</span>
                  </Button>
                  <Button variant="outline" className="h-auto flex-col gap-2 p-6">
                    <BookOpen className="h-8 w-8" />
                    <span>Create Course</span>
                  </Button>
                  <Button variant="outline" className="h-auto flex-col gap-2 p-6">
                    <MessageSquare className="h-8 w-8" />
                    <span>Discussion Topic</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'manage':
        return (
          <div className="space-y-6">
            <Card className="bg-white border border-gray-200">
              <CardHeader>
                <CardTitle className="text-black">Your Content</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {recentContent.map((content, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <h3 className="font-semibold text-black">{content.title}</h3>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                        <span>Views: {content.views}</span>
                        <span>Rating: {content.rating}/5</span>
                        <span>Last edited: {content.lastEdited}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={
                        content.status === 'published' ? 'default' :
                        content.status === 'in-review' ? 'secondary' : 'outline'
                      }>
                        {content.status}
                      </Badge>
                      <Button size="sm" variant="outline">Edit</Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        );

      case 'review':
        return (
          <div className="space-y-6">
            <Card className="bg-white border border-gray-200">
              <CardHeader>
                <CardTitle className="text-black">Review Queue</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">No items pending review</p>
              </CardContent>
            </Card>
          </div>
        );

      case 'analytics':
        return (
          <div className="space-y-6">
            <Card className="bg-white border border-gray-200">
              <CardHeader>
                <CardTitle className="text-black">Content Analytics</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Analytics dashboard coming soon</p>
              </CardContent>
            </Card>
          </div>
        );

      case 'settings':
        return (
          <div className="space-y-6">
            <Card className="bg-white border border-gray-200">
              <CardHeader>
                <CardTitle className="text-black">Creator Settings</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Settings panel coming soon</p>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return (
          <div className="space-y-6">
            <Card className="bg-white border border-gray-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-2xl font-bold text-black mb-2">Content Creator Dashboard</h1>
                    <p className="text-gray-600">Create, manage, and track your educational content</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Creator Level</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Star className="h-4 w-4 text-yellow-500" />
                      <span className="text-sm font-medium text-black">Expert</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {contentStats.map((stat, index) => (
                <Card key={index} className="bg-white border border-gray-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${stat.color}`}>
                        <stat.icon className="h-5 w-5 text-gray-700" />
                      </div>
                      <div className="flex-1">
                        <p className="text-2xl font-bold text-black">{stat.value}</p>
                        <p className="text-sm text-gray-600">{stat.label}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <TrendingUp className="h-3 w-3 text-green-500" />
                          <span className="text-xs text-green-600">{stat.trend}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-white border border-gray-200">
                <CardHeader>
                  <CardTitle className="text-black">Recent Content</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {recentContent.map((content, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <h3 className="font-medium text-black">{content.title}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Progress value={content.engagement} className="w-24" />
                          <span className="text-xs text-gray-600">{content.engagement}% engagement</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{content.lastEdited}</p>
                      </div>
                      <Badge variant={
                        content.status === 'published' ? 'default' :
                        content.status === 'in-review' ? 'secondary' : 'outline'
                      }>
                        {content.status}
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="bg-white border border-gray-200">
                <CardHeader>
                  <CardTitle className="text-black">Notifications</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {notifications.map((notification, index) => (
                    <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                      <div className={`p-2 rounded-lg ${
                        notification.type === 'success' ? 'bg-green-100' : 'bg-blue-100'
                      }`}>
                        {notification.type === 'success' ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-blue-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-black">{notification.message}</p>
                        <p className="text-sm text-gray-500">{notification.time}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            <Card className="bg-white border border-gray-200">
              <CardHeader>
                <CardTitle className="text-black">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Button className="h-auto flex-col gap-2 p-4">
                    <Edit3 className="h-6 w-6" />
                    <span>Write Article</span>
                  </Button>
                  <Button variant="outline" className="h-auto flex-col gap-2 p-4">
                    <FileText className="h-6 w-6" />
                    <span>Manage Content</span>
                  </Button>
                  <Button variant="outline" className="h-auto flex-col gap-2 p-4">
                    <BarChart3 className="h-6 w-6" />
                    <span>View Analytics</span>
                  </Button>
                  <Button variant="outline" className="h-auto flex-col gap-2 p-4">
                    <ThumbsUp className="h-6 w-6" />
                    <span>Review Feedback</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        );
    }
  };

  const sidebarBottom = (
    <button className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-gray-600 hover:bg-gray-200 hover:text-black transition-all duration-200">
      <Eye className="h-5 w-5 flex-shrink-0" />
      <span className="overflow-hidden transition-all duration-300 whitespace-nowrap">
        Preview Mode
      </span>
    </button>
  );

  return (
    <DashboardLayout
      title="Content Hub"
      menuItems={menuItems}
      activeTab={activeTab}
      onTabChange={(tabId: string) => setActiveTab(tabId as ContentTabType)}
      sidebarBottom={sidebarBottom}
    >
      {renderMainContent()}
    </DashboardLayout>
  );
}
